import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getHubMismatchRedirect } from '../utils/hubRoutes';
import ForceReauth from './ForceReauth';

/**
 * Requires authentication and a selected organization claim.
 * Validates URL orgId matches the claim for hub-scoped routes, and that the
 * selected organization belongs to this hub (otherwise redirects to the
 * organization's own hub).
 */
const OrgProtectedRoute = ({ children, hubPrefix = 'app' }) => {
    const { currentUser, userClaims, organization, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!currentUser) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!userClaims?.orgId) {
        return <Navigate to="/hubs" state={{ from: location }} replace />;
    }

    if (!organization?.id) {
        return <ForceReauth />;
    }

    const hubRedirect = getHubMismatchRedirect(organization, hubPrefix);
    if (hubRedirect) {
        return <Navigate to={hubRedirect} replace />;
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments[0] === hubPrefix && pathSegments[1]) {
        const urlOrgId = parseInt(pathSegments[1], 10);
        const userOrgId = parseInt(userClaims.orgId, 10);
        if (!Number.isNaN(urlOrgId) && !Number.isNaN(userOrgId) && urlOrgId !== userOrgId) {
            const remainingPath = pathSegments.slice(2).join('/');
            const redirectPath = remainingPath
                ? `/${hubPrefix}/${userOrgId}/${remainingPath}${location.search}${location.hash}`
                : `/${hubPrefix}/${userOrgId}${location.search}${location.hash}`;
            return <Navigate to={redirectPath} replace />;
        }
    }

    return children;
};

export default OrgProtectedRoute;
